import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { TExpertiseItem } from '@onesource/schemas';
import { useGetExpertiseQuery } from '@onesource/services';
import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

type TOption = { id: string; label: string; group: string };

/** Group shown for stored values that aren't in the lookup table (legacy free text). */
const LEGACY_GROUP = 'Other';

type TProps = Omit<TextFieldProps, 'name'> & {
  name: string;
  maxSelections?: number;
};

/**
 * Multi-select picker over `pro_directory_expertise`, grouped by the parent rows.
 *
 * The field value is always `string[]`. New selections store stringified ids; values
 * predating the lookup table are free text that was never backfilled, so they are
 * surfaced as synthetic options under "Other" rather than silently dropped — without
 * that, opening an older profile would blank the field and wipe it on save.
 */
export const RHFExpertiseAutocomplete = ({ name, disabled, label, helperText, maxSelections }: TProps) => {
  const { control } = useFormContext();
  const { data: expertise = [], isLoading } = useGetExpertiseQuery();

  const options = useMemo<TOption[]>(() => {
    const parents = new Map<number, string>();
    expertise.filter(item => item.parentId === null).forEach(item => parents.set(item.id, item.name));

    return (
      expertise
        .filter((item): item is TExpertiseItem & { parentId: number } => item.parentId !== null)
        .map(item => ({ id: String(item.id), label: item.name, group: parents.get(item.parentId) ?? '' }))
        // MUI Autocomplete groupBy renders a fresh group header every time the group
        // value changes while walking the list — a group whose options are not
        // contiguous shows its header twice. The API returns options ordered by id,
        // and ids are not grouped by parent, so sort by group here to keep each
        // group's options together.
        .sort((a, b) => a.group.localeCompare(b.group) || a.label.localeCompare(b.label))
    );
  }, [expertise]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const currentIds: string[] = Array.isArray(field.value) ? field.value : [];

        // Keep unmatched stored values selectable/removable. Skipped while the query is
        // in flight, otherwise every real id would briefly render as a legacy chip.
        const legacyOptions: TOption[] = isLoading
          ? []
          : currentIds
              .filter(id => !options.some(option => option.id === id))
              .map(value => ({ id: value, label: value, group: LEGACY_GROUP }));

        const allOptions = [...options, ...legacyOptions];
        const selectedOptions = allOptions.filter(option => currentIds.includes(option.id));

        return (
          <Autocomplete
            multiple
            options={allOptions}
            groupBy={option => option.group}
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedOptions}
            onChange={(_, values) => {
              if (maxSelections !== undefined && values.length > maxSelections) {
                return;
              }
              field.onChange(values.map(value => value.id));
            }}
            disabled={disabled || isLoading}
            loading={isLoading}
            renderTags={(values, getTagProps) =>
              values.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return <Chip key={key} label={option.label} size="small" {...tagProps} />;
              })
            }
            renderInput={params => (
              <TextField
                {...params}
                inputRef={field.ref}
                label={label ?? 'Expertise'}
                error={!!error}
                helperText={error?.message ?? helperText}
                autoComplete="off"
              />
            )}
          />
        );
      }}
    />
  );
};
